/**
 * ExportButton.js
 *
 * Small dropdown button that downloads the current document as either
 * PDF or DOCX. Used on the CV Variant preview and Cover Letter preview.
 *
 * Props:
 *   - variantId | letterId  (exactly one must be set; used to pick the endpoint)
 *   - title                 (used only for the toast/error UI)
 *   - disabled
 */

import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Download, Loader2, FileText, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function downloadBlob(path, fallbackName) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated.');

  const res = await fetch(`${BACKEND_URL}${path}`, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (!res.ok) {
    let msg = `Export failed (${res.status})`;
    try {
      const j = await res.json();
      msg = j.detail || msg;
    } catch { /* binary response or no body */ }
    throw new Error(msg);
  }

  // Extract filename from Content-Disposition, with a safe fallback
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename="([^"]+)"/);
  const filename = m ? m[1] : fallbackName;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function ExportButton({ variantId, letterId, title, disabled }) {
  const [busy, setBusy] = useState(false);

  const trigger = async (format) => {
    if (!variantId && !letterId) return;
    setBusy(true);
    try {
      const base = variantId
        ? `/api/cv-variants/${variantId}/export`
        : `/api/cover-letters/${letterId}/export`;
      const ext = format === 'docx' ? 'docx' : 'pdf';
      await downloadBlob(`${base}?format=${format}`, `${(title || 'document').slice(0, 60)}.${ext}`);
      toast.success(`Downloaded ${format.toUpperCase()}.`);
    } catch (err) {
      toast.error(err?.message || 'Export failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-ndor-ink-muted"
          disabled={disabled || busy}
          data-testid="export-button"
        >
          {busy ? (
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
          ) : (
            <Download className="h-3 w-3 mr-1" />
          )}
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => trigger('pdf')}
          data-testid="export-pdf"
        >
          <FileText className="h-4 w-4 mr-2" /> PDF
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => trigger('docx')}
          data-testid="export-docx"
        >
          <FileDown className="h-4 w-4 mr-2" /> Word (.docx)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
